// Beta-0.5D-A · <DangerZone> — the standardised destructive-action pattern.
//
// Rules:
//   • Every destructive action must have consequence-first wording,
//     an estimated impact, an affected count, an explicit confirmation,
//     a permission check (caller enforces), and an audit record.
//   • Never place a destructive action beside a safe action visually.
//   • Red section, clear warnings, impact summary.
//
// Usage:
//   <DangerZone titleJa="危険な操作">
//     <DangerAction
//       titleJa="検索インデックスから除外対象を削除"
//       consequenceJa="この操作は取り消せません。NAS上のファイルは削除しません。"
//       impact={{ affected: 42, unit: '件', unrecoverable: true }}
//       previewButton={{ labelJa: '影響を確認', onPreview: async () => …preview API… }}
//       confirmPromptJa="削除する"
//       onConfirm={async () => …destructive API…}
//     />
//   </DangerZone>

import { useState } from 'react';
import { Warning, ArrowsClockwise, CircleNotch } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export function DangerZone({ titleJa = '危険な操作', descriptionJa, children,
                             testid = 'danger-zone' }) {
  return (
    <section
      data-testid={testid}
      className="mt-8 rounded-lg border-2 border-rose-200 bg-rose-50/40 overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-rose-200 bg-rose-100/60">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-rose-800">
          <Warning size={13} weight="fill" /> {titleJa}
        </p>
        {descriptionJa && (
          <p className="mt-1 text-[12px] text-rose-900/80">{descriptionJa}</p>
        )}
      </header>
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

// One destructive action row. `previewButton` is optional — when given the
// row can compute the affected count before the admin is asked to confirm.
export function DangerAction({
  titleJa, consequenceJa, impact,
  previewButton,   // { labelJa, onPreview: () => Promise<{affected, ...}> }
  confirmPromptJa = '削除する',
  onConfirm,       // async (): Promise<{ok:true, deleted?:number, ...}>
  disabled = false,
  testid,
}) {
  const [preview, setPreview] = useState(impact || null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [runErr, setRunErr] = useState(null);
  const [open, setOpen] = useState(false);

  const runPreview = async () => {
    if (!previewButton?.onPreview) return;
    setPreviewing(true); setPreviewErr(null);
    try { setPreview(await previewButton.onPreview()); }
    catch (e) { setPreviewErr(e?.response?.data?.detail?.message
                              || e?.message || '影響の計算に失敗しました'); }
    finally { setPreviewing(false); }
  };

  const runConfirm = async () => {
    setRunning(true); setRunErr(null); setResult(null);
    try {
      const r = await onConfirm();
      setResult(r || { ok: true });
      setOpen(false);
    } catch (e) {
      setRunErr(e?.response?.data?.detail?.message
                || e?.message || '実行に失敗しました');
    } finally { setRunning(false); }
  };

  return (
    <div data-testid={testid} className="rounded-md border border-rose-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-rose-900">{titleJa}</p>
          <p className="text-[11.5px] text-rose-900/80 mt-1">{consequenceJa}</p>
          {preview && (
            <p className="mt-1.5 text-[11.5px] font-medium text-rose-900"
              data-testid={testid ? `${testid}-impact` : undefined}>
              影響: <span className="tabular-nums font-bold">
                {(preview.affected ?? '?').toLocaleString?.() ?? preview.affected}
              </span>
              {preview.unit ? ` ${preview.unit}` : ''}
              {preview.unrecoverable ? ' · 元に戻せません' : ''}
              {preview.detailJa ? ` · ${preview.detailJa}` : ''}
            </p>
          )}
          {previewErr && (
            <p className="mt-1 text-[11px] text-rose-800">
              <Warning size={11} className="inline mr-0.5" />{previewErr}
            </p>
          )}
          {result && (
            <p className="mt-1.5 text-[11.5px] font-medium text-emerald-800"
              data-testid={testid ? `${testid}-result` : undefined}>
              完了しました
              {typeof result.deleted === 'number'
                ? ` · ${result.deleted.toLocaleString('ja-JP')} 件削除`
                : ''}
              {result.message_ja ? ` · ${result.message_ja}` : ''}
            </p>
          )}
          {runErr && (
            <p className="mt-1.5 text-[11.5px] text-rose-800">
              <Warning size={11} className="inline mr-0.5" />{runErr}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {previewButton && (
            <Button size="sm" variant="outline"
              disabled={previewing || disabled}
              onClick={runPreview}
              className="h-8 text-[11.5px] border-rose-300 text-rose-800 hover:bg-rose-50"
              data-testid={testid ? `${testid}-preview-btn` : undefined}>
              {previewing
                ? <CircleNotch size={11} className="animate-spin mr-1" />
                : <ArrowsClockwise size={11} className="mr-1" />}
              {previewButton.labelJa || '影響を確認'}
            </Button>
          )}
          <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
              <button type="button"
                disabled={disabled}
                data-testid={testid ? `${testid}-confirm-open` : undefined}
                className="h-8 px-3 rounded-md text-[11.5px] font-semibold text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed">
                {confirmPromptJa}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid={testid ? `${testid}-dialog` : undefined}>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-rose-900">
                  <Warning size={16} weight="fill" className="inline mr-1 -mt-1" />
                  {titleJa}
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2">
                  <span className="block text-[13px] text-zinc-800">{consequenceJa}</span>
                  {preview && (
                    <span className="block rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] font-medium text-rose-900"
                      data-testid={testid ? `${testid}-dialog-impact` : undefined}>
                      影響: <span className="tabular-nums font-bold">
                        {(preview.affected ?? '?').toLocaleString?.() ?? preview.affected}
                      </span>
                      {preview.unit ? ` ${preview.unit}` : ''}
                      {preview.unrecoverable ? ' · 元に戻せません' : ''}
                      {preview.detailJa ? ` · ${preview.detailJa}` : ''}
                    </span>
                  )}
                  <span className="block text-[11.5px] text-zinc-500">
                    この処理は監査ログに記録されます。
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  data-testid={testid ? `${testid}-dialog-cancel` : undefined}
                  disabled={running}>キャンセル</AlertDialogCancel>
                <AlertDialogAction
                  data-testid={testid ? `${testid}-dialog-confirm` : undefined}
                  disabled={running}
                  onClick={runConfirm}
                  className="bg-rose-700 hover:bg-rose-800 focus:ring-rose-500">
                  {running
                    ? <><CircleNotch size={11} className="animate-spin mr-1" />実行中…</>
                    : confirmPromptJa}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
